// Canonical CV language proficiency (audit B12 / F11).
//
// The stored value of a language level is one of five codes, deliberately the
// English tokens the native editor has always written and every shipped client
// already parses ("Arabic (Native)"), so storage and the wire format do not
// change. CEFR levels (A1-C2) are kept as their own language-neutral codes.
//
// Display labels are rendered from the code, per locale, at the edge. Anything
// that was ever stored as a display label (the Arabic web editor wrote its
// translated label 2026-05-26 to 2026-07-15) or as a longer English phrase maps
// back to its code through `normalizeCvLanguageProficiency`; everything else
// fails closed to "" so contact data and prose never pass as a level.
//
// Imported by the API, mobile, public renderers and node:test. Keep it free of
// runtime imports and non-erasable TypeScript syntax.

export const CV_LANGUAGE_PROFICIENCY_CODES = [
  "Native",
  "Fluent",
  "Professional",
  "Intermediate",
  "Basic",
] as const;

export type CvLanguageProficiencyCode =
  (typeof CV_LANGUAGE_PROFICIENCY_CODES)[number];

export const CV_LANGUAGE_CEFR_LEVELS = [
  "A1",
  "A2",
  "B1",
  "B2",
  "C1",
  "C2",
] as const;

export type CvLanguageCefrLevel = (typeof CV_LANGUAGE_CEFR_LEVELS)[number];

/** A canonical stored level: one of the five codes or a CEFR level. */
export type CvLanguageProficiency =
  | CvLanguageProficiencyCode
  | CvLanguageCefrLevel;

/** Longest proficiency text accepted at all (same cap as the API schema). */
export const CV_LANGUAGE_PROFICIENCY_MAX_LENGTH = 64;

export type CvLanguageProficiencyLocale = "en" | "ar";

/**
 * Document labels per locale. The Arabic set is the one exports and the public
 * renderer have always printed, so documents read exactly as before.
 */
export const CV_LANGUAGE_PROFICIENCY_LABELS: Record<
  CvLanguageProficiencyLocale,
  Record<CvLanguageProficiencyCode, string>
> = {
  en: {
    Native: "Native",
    Fluent: "Fluent",
    Professional: "Professional",
    Intermediate: "Intermediate",
    Basic: "Basic",
  },
  ar: {
    Native: "اللغة الأم",
    Fluent: "طلاقة",
    Professional: "احترافية",
    Intermediate: "متوسطة",
    Basic: "أساسية",
  },
};

// Every spelling a level has been stored or parsed as, by code. English: the
// phrases the API sanitizer accepted verbatim before B12 plus the mobile
// normalizer's list. Arabic: the three vocabularies the app has shipped (web
// editor i18n labels, export/renderer labels, the ar/seeker.ts level set) and
// the gendered/definite forms of those same words. Interpretive words such as
// grading terms ("good", "excellent") are deliberately absent.
const LEVEL_SYNONYMS: Record<CvLanguageProficiencyCode, readonly string[]> = {
  Native: [
    "native",
    "native or bilingual",
    "native or bilingual proficiency",
    "bilingual",
    "mother tongue",
    "mothertongue",
    "mother language",
    "motherlanguage",
    "mother lang",
    "motherlang",
    "لغة أم",
    "اللغة الأم",
    "لغة الأم",
  ],
  Fluent: [
    "fluent",
    "full professional",
    "full professional proficiency",
    "طليق",
    "طليقة",
    "طلاقة",
  ],
  Professional: [
    "professional",
    "professional proficiency",
    "professional working proficiency",
    "working proficiency",
    "advanced",
    "proficient",
    "مهني",
    "مهنية",
    "احترافي",
    "احترافية",
    "متقدم",
    "متقدمة",
  ],
  Intermediate: [
    "intermediate",
    "upper intermediate",
    "upperintermediate",
    "limited working proficiency",
    "conversational",
    "متوسط",
    "متوسطة",
  ],
  Basic: [
    "basic",
    "elementary",
    "elementary proficiency",
    "beginner",
    "pre intermediate",
    "preintermediate",
    "أساسي",
    "أساسية",
    "مبتدئ",
    "مبتدئة",
  ],
};

// Arabic marks that never change a word: harakat/tanween/shadda/sukun,
// Quranic annotation marks, superscript alef and tatweel.
const ARABIC_IGNORABLE_RE =
  /[ؐ-ًؚ-ٰٟۖ-ۭـ]/gu;
// Hamza-carrying and wasla alef forms read as a bare alef.
const ARABIC_ALEF_VARIANTS_RE = /[آأإٱ]/gu;
// Spaces, underscores and hyphen/dash punctuation separate words the same way.
const WORD_SEPARATOR_RE = /[\s_\-‐-―]+/gu;
const CEFR_RE = /^[abc][12]$/;

/** Comparison key: case, spacing and Arabic orthographic variants folded. */
function foldLevelKey(value: string): string {
  return value
    .normalize("NFKC")
    .replace(ARABIC_IGNORABLE_RE, "")
    .replace(ARABIC_ALEF_VARIANTS_RE, "ا")
    .replace(/ة/gu, "ه")
    .replace(/ى/gu, "ي")
    .toLowerCase()
    .replace(WORD_SEPARATOR_RE, " ")
    .trim();
}

const LEVEL_BY_KEY: ReadonlyMap<string, CvLanguageProficiencyCode> = new Map(
  CV_LANGUAGE_PROFICIENCY_CODES.flatMap((code) =>
    LEVEL_SYNONYMS[code].map(
      (synonym) => [foldLevelKey(synonym), code] as const,
    ),
  ),
);

function lookupLevel(text: string): CvLanguageProficiency | "" {
  const key = foldLevelKey(text);
  if (CEFR_RE.test(key)) return key.toUpperCase() as CvLanguageCefrLevel;
  return LEVEL_BY_KEY.get(key) ?? "";
}

/** True for the five codes and CEFR levels, exactly as stored. */
export function isCvLanguageProficiency(
  value: unknown,
): value is CvLanguageProficiency {
  return (
    typeof value === "string" &&
    ((CV_LANGUAGE_PROFICIENCY_CODES as readonly string[]).includes(value) ||
      (CV_LANGUAGE_CEFR_LEVELS as readonly string[]).includes(value))
  );
}

/**
 * Map any known spelling of a level (code, English phrase, CEFR in any case,
 * any shipped Arabic label) to its canonical code. Unknown text returns "".
 *
 * A recognized level followed only by a non-Latin gloss keeps the level
 * ("Native العربية اللغة الأم" is "Native"); a suffix carrying Latin letters or
 * digits fails closed so appended prose and contact data never survive.
 */
export function normalizeCvLanguageProficiency(
  value: unknown,
): CvLanguageProficiency | "" {
  if (typeof value !== "string") return "";
  const text = value
    .normalize("NFKC")
    .replace(/\s+/gu, " ")
    .trim()
    .replace(/^\(|\)$/gu, "")
    .trim();
  if (!text || text.length > CV_LANGUAGE_PROFICIENCY_MAX_LENGTH) return "";

  const direct = lookupLevel(text);
  if (direct) return direct;

  const words = text.split(" ");
  for (let split = words.length - 1; split >= 1; split -= 1) {
    const gloss = words.slice(split).join(" ");
    if (!/\p{L}/u.test(gloss) || /[\p{Script=Latin}\p{N}]/u.test(gloss)) {
      continue;
    }
    const level = lookupLevel(words.slice(0, split).join(" "));
    if (level) return level;
  }
  return "";
}

/** "ar", "ar-AE", "AR" read as Arabic; everything else as English. */
export function resolveCvLanguageProficiencyLocale(
  locale: string | null | undefined,
): CvLanguageProficiencyLocale {
  return typeof locale === "string" &&
    locale.trim().toLowerCase().startsWith("ar")
    ? "ar"
    : "en";
}

/**
 * The display label of a level for a document locale. CEFR levels render as
 * themselves; unknown values render as "".
 */
export function formatCvLanguageProficiency(
  value: unknown,
  locale: string | null | undefined,
): string {
  const level = normalizeCvLanguageProficiency(value);
  if (!level) return "";
  if ((CV_LANGUAGE_CEFR_LEVELS as readonly string[]).includes(level)) {
    return level;
  }
  return CV_LANGUAGE_PROFICIENCY_LABELS[
    resolveCvLanguageProficiencyLocale(locale)
  ][level as CvLanguageProficiencyCode];
}

const LANGUAGE_ENTRY_RE = /^(.+?)\s*\((.+)\)\s*$/u;

/**
 * Localize the stored "Language (Level)" display string for a document locale.
 * The level renders from its code; a bare name, a CEFR level or an unknown
 * level is returned exactly as given.
 */
export function localizeCvLanguageEntry(
  entry: string,
  locale: string | null | undefined,
): string {
  const match = entry.match(LANGUAGE_ENTRY_RE);
  if (!match) return entry;
  const level = match[2].trim();
  const label = formatCvLanguageProficiency(level, locale);
  if (!label || label === level) return entry;
  return `${match[1].trim()} (${label})`;
}
